import { useEffect, useRef, useState } from 'react';
import { Link, NavLink, Outlet, useLocation } from 'react-router';
import { useQuery } from '@tanstack/react-query';
import { aiApi } from '../lib/aiApi.ts';
import { useIsDesktop } from '../lib/media.ts';
import { ErrorBoundary } from './ErrorBoundary.tsx';
import type { Role } from '@aibify/core';
import { logout, roleAtLeast, useSession } from '../lib/auth.ts';
import { T } from '../lib/strings.ts';
import { cn } from '../lib/utils.ts';
import { Button } from './ui/button.tsx';
import { Sheet } from './ui/sheet.tsx';
import { ThemeToggle, useTheme } from './ThemeToggle.tsx';
import { ConsentDrawer, consentHref } from './ConsentDrawer.tsx';
import { MailConnectDrawer } from './MailConnectDrawer.tsx';
import { NotificationsDrawer, NOTIFY_PARAM } from './NotificationsDrawer.tsx';
import { Logo } from './brand/Logo.tsx';
import { CompanyLogo } from './brand/CompanyLogo.tsx';
import { useBrand, useDocTitle, type Brand } from '../lib/brand.ts';
import { IconGrid, IconInbox, IconBell, IconLogout, IconMenu, IconShield, IconSparkles, IconUsers } from './brand/icons.tsx';
import { usersApi } from '../lib/usersApi.ts';

// מעטפת האפליקציה. דסקטופ (≥768, web-screen-budget): פס ניווט צר (4.5rem) — לוגו-אייקון, יעדים (אייקון + תווית
// מתחת), ולמטה אישורים ממתינים (תג), לוגו החברה הקטן ומשתמש בראשי תיבות ושם פרטי (web-instance-identity) — לחיצה פותחת
// תפריט: שם מלא, אימייל, תפקיד, הסביבה, ערכת צבע, יציאה; בלי סרגל עליון ובלי תקרת רוחב (פרוזה מוגבלת בעמוד).
// מובייל: סרגל עליון (תפריט, לוגו, אישורים, ערכת צבע) + סרגל ניווט תחתון; בתפריט הצד, בתחתית, המשתמש ומתחתיו לוגו החברה
// ושמה. בבינה-1/2 השיחה במסך מלא (11 §4) — הסרגל התחתון מוסתר והניווט זמין מהתפריט. ניווט לפי role מה-JWT — רק הסתרה;
// האכיפה ב-api. כותרת הלשונית: `<עמוד> · <שם הסביבה>`.
const NAV: Array<{ to: string; label: string; min: Role; Icon: typeof IconGrid }> = [
  { to: '/artifacts', label: T.nav.artifacts, min: 'member', Icon: IconGrid },
  { to: '/ai2', label: T.nav.ai2, min: 'creator', Icon: IconSparkles },
  { to: '/ai1', label: T.nav.ai1, min: 'admin', Icon: IconShield },
];
/** 20: "חיבור Gmail" — drawer זמני בלבד, בלי כניסה קבועה בתפריט (המשתמש, 2026-10-15): נפתח כשצריך (no_mailbox/reauth), מלשונית הממשקים, או מ-?mail=manage. */

/** כותרת הלשונית לפי הנתיב (יעד הניווט שהנתיב בתוכו). */
const PAGES: Array<{ to: string; label: string }> = [...NAV, { to: '/users', label: T.nav.users }];
const pageLabel = (pathname: string) => PAGES.find((p) => pathname === p.to || pathname.startsWith(`${p.to}/`))?.label ?? T.brand;

/** ראשי תיבות לאווטאר (שתי מילים ראשונות, בלי סוגריים). */
function initials(name = '') {
  return name
    .replace(/[()]/g, '')
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0])
    .join('');
}

/** פס בדסקטופ: ראשי תיבות ושם פרטי גלוי; לחיצה פותחת את תפריט המשתמש (בצד הפס, מיושר לתחתית). */
function UserMenu({ brand }: { brand: Brand | null }) {
  const s = useSession();
  const loc = useLocation();
  const theme = useTheme();
  const [open, setOpen] = useState(false);
  const btn = useRef<HTMLButtonElement>(null);
  const menu = useRef<HTMLDivElement>(null);
  const name = s?.user.nameHe ?? '';
  const role = T.roles[s?.user.role ?? 'member'];
  const first = name.trim().split(/\s+/)[0] ?? '';
  useEffect(() => setOpen(false), [loc.pathname]);
  useEffect(() => {
    if (!open) return;
    menu.current?.querySelector<HTMLElement>('[role="menuitem"]')?.focus();
    const onDown = (e: PointerEvent) => {
      if (!menu.current?.contains(e.target as Node) && !btn.current?.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      setOpen(false);
      btn.current?.focus();
    };
    document.addEventListener('pointerdown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('pointerdown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);
  const item = 'flex min-h-9 w-full cursor-pointer items-center rounded-sm px-2.5 text-start text-[13px] text-foreground transition-colors hover:bg-raised focus-visible:bg-raised';
  return (
    <>
      <button
        ref={btn}
        type="button"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={`${name} · ${role}`}
        title={`${name} · ${role}`}
        onClick={() => setOpen((o) => !o)}
        className="flex min-h-11 w-16 cursor-pointer flex-col items-center gap-0.5 rounded-md p-1 text-[11px] leading-tight text-sidebar-foreground transition-colors hover:bg-sidebar-active/60"
        data-testid="whoami"
      >
        <span aria-hidden="true" className="grid size-8 place-items-center rounded-full bg-action text-xs font-semibold text-action-foreground">
          {initials(name)}
        </span>
        <span aria-hidden="true" className="max-w-full truncate" data-testid="whoami-first">
          {first}
        </span>
      </button>
      {open && (
        <div ref={menu} role="menu" aria-label={name} className="fixed start-20 bottom-[calc(var(--ab-foot)+0.75rem)] z-40 w-[260px] rounded-md border border-border bg-card p-1.5 text-foreground shadow-lg" data-testid="user-menu">
          <div className="mb-1 border-b border-line px-2.5 pt-2.5 pb-3">
            <b className="block text-sm font-semibold">{name}</b>
            <small className="block text-xs text-muted-foreground">
              <bdi>{s?.user.email}</bdi>
            </small>
            <small className="block text-xs text-muted-foreground">{role}</small>
            <div className="mt-3" data-testid="user-menu-env">
              <CompanyLogo brand={brand} size={96} />
            </div>
          </div>
          <Link role="menuitem" className={item} to={{ search: `?${NOTIFY_PARAM}=1` }} onClick={() => setOpen(false)} data-testid="notify-open">
            {T.push.nav}
          </Link>
          <button type="button" role="menuitem" className={item} onClick={theme.next} data-testid="theme-toggle" data-theme-choice={theme.theme}>
            {theme.label}
          </button>
          <button type="button" role="menuitem" className={item} onClick={() => logout()}>
            {T.logout}
          </button>
        </div>
      )}
    </>
  );
}

function UserCard({ tone = 'sidebar' }: { tone?: 'sidebar' | 'plain' }) {
  const s = useSession();
  const name = s?.user.nameHe ?? '';
  return (
    <div className="flex min-w-0 items-center gap-3" data-testid={tone === 'sidebar' ? 'whoami' : undefined}>
      <span aria-hidden="true" className="grid size-9 shrink-0 place-items-center rounded-full bg-action text-sm font-bold text-action-foreground">
        {initials(name)}
      </span>
      <span className="flex min-w-0 flex-col leading-tight">
        <span className="truncate text-sm font-semibold">{name}</span>
        <span className={cn('truncate text-xs', tone === 'sidebar' ? 'text-sidebar-muted' : 'text-muted-foreground')}>{T.roles[s?.user.role ?? 'member']}</span>
      </span>
    </div>
  );
}

export function Layout() {
  const s = useSession();
  const loc = useLocation();
  // users-delegation: "משתמשים" לפי GET /api/users/me (admin או בעל הרשאת האצלה, מהמסד), לא לפי ה-JWT; הסתרה בלבד, האכיפה בשרת
  const usersMe = useQuery({ queryKey: ['users-me'], queryFn: usersApi.me, staleTime: 60_000, retry: false });
  const items = [
    ...NAV.filter((n) => roleAtLeast(s?.user.role, n.min)),
    ...(usersMe.data?.canManageUsers ? [{ to: '/users', label: T.nav.users, min: 'member' as Role, Icon: IconUsers }] : []),
  ];
  const [menuOpen, setMenuOpen] = useState(false);
  const desktop = useIsDesktop();
  const brand = useBrand();
  useDocTitle(pageLabel(loc.pathname));
  // שיחות (ai1/ai2) במובייל — מסך מלא, בלי הסרגל התחתון
  const fullScreen = /^\/ai[12](\/|$)/.test(loc.pathname);
  useEffect(() => setMenuOpen(false), [loc.pathname]);
  // תג "אישורים ממתינים": אותה שאילתה כמו ה-drawer (['consents']); מתרענן אחרי תוצאת כלי בשיחה (Ai.tsx) ואחרי החלטה
  const consents = useQuery({ queryKey: ['consents'], queryFn: aiApi.listConsents, staleTime: 30_000, retry: false });
  const pendingConsents = consents.data?.consents.length ?? 0;
  const consentsLabel = pendingConsents ? T.shell.consentsCount(pendingConsents) : T.shell.consents;
  const consentsBadge =
    pendingConsents > 0 ? (
      <span aria-hidden="true" className="absolute -top-0.5 end-1 min-w-5 rounded-sm bg-action px-1 text-center text-[11px] font-semibold leading-5 tabular-nums text-action-foreground" data-testid="consents-badge">
        {pendingConsents}
      </span>
    ) : null;
  // פריט בפס: אייקון ותווית 11px מתחת; מטרת לחיצה 56px גובה ברוחב הפס
  const railItem = 'relative flex min-h-14 w-full flex-col items-center justify-center gap-1 rounded-sm px-1 text-[11px] font-semibold leading-tight text-sidebar-muted transition-colors hover:bg-sidebar-active/60 hover:text-sidebar-foreground';

  return (
    <div className="min-h-(--ab-screen) md:grid md:grid-cols-[4.5rem_minmax(0,1fr)]">
      <a
        href="#main"
        className="sr-only z-50 rounded-sm bg-action px-4 py-2 font-semibold text-action-foreground focus:not-sr-only focus:fixed focus:start-4 focus:top-3"
      >
        {T.shell.skip}
      </a>

      {/* פס ניווט (דסקטופ) / סרגל ניווט תחתון (מובייל) — אותו <nav> אחד */}
      <aside
        className={cn(
          'z-30 [--logo-gap:var(--sidebar)] [--logo-ink:var(--sidebar-foreground)]',
          'fixed inset-x-0 bottom-(--ab-foot) border-t border-border bg-card',
          'md:sticky md:top-0 md:flex md:h-(--ab-screen) md:flex-col md:items-center md:gap-3 md:border-t-0 md:border-e md:border-sidebar-border md:bg-sidebar md:px-1.5 md:py-3 md:text-sidebar-foreground',
          fullScreen && 'max-md:hidden',
        )}
        data-testid="app-nav"
      >
        {desktop && (
          <Link to="/artifacts" aria-label={T.shell.home} className="grid min-h-11 min-w-11 place-items-center rounded-sm" data-testid="rail-logo">
            <Logo variant="mark" bold className="h-8" />
          </Link>
        )}
        <nav aria-label={T.shell.mainNav} className="md:w-full md:flex-1">
          <ul className="m-0 flex list-none justify-around gap-1 p-0 md:flex-col md:justify-start" data-testid="main-nav">
            {items.map(({ to, label, Icon }) => (
              <li key={to} className="flex-1 md:flex-none">
                <NavLink
                  to={to}
                  className={({ isActive }) =>
                    cn(
                      'group relative flex min-h-14 flex-col items-center justify-center gap-0.5 rounded-sm px-2 text-[13px] font-semibold transition-colors',
                      'md:gap-1 md:px-1 md:text-[11px] md:leading-tight',
                      isActive
                        ? 'text-primary md:bg-sidebar-active md:text-sidebar-active-foreground'
                        : 'text-muted-foreground hover:text-foreground md:text-sidebar-muted md:hover:bg-sidebar-active/60 md:hover:text-sidebar-foreground',
                    )
                  }
                >
                  {({ isActive }) => (
                    <>
                      <span
                        aria-hidden="true"
                        className={cn(
                          'absolute inset-x-6 top-0 h-0.5 rounded-sm bg-action transition-opacity md:hidden',
                          isActive ? 'opacity-100' : 'opacity-0',
                        )}
                      />
                      <Icon size={22} className="md:size-5" />
                      <span className="md:max-w-full md:text-center">{label}</span>
                    </>
                  )}
                </NavLink>
              </li>
            ))}
          </ul>
        </nav>
        {desktop && (
          <div className="flex w-full flex-col items-center gap-1 border-t border-sidebar-border pt-2" data-testid="rail-bottom">
            <Link to={consentHref(loc.search, 'all')} aria-label={consentsLabel} title={consentsLabel} className={railItem} data-testid="rail-consents">
              <IconInbox size={20} />
              <span aria-hidden="true">{T.shell.consentsShort}</span>
              {consentsBadge}
            </Link>
            <CompanyLogo brand={brand} size={40} className="mt-1.5 mb-1" />
            <UserMenu brand={brand} />
          </div>
        )}
      </aside>

      <div className="flex min-w-0 flex-col">
        {/* סרגל עליון — במובייל בלבד (בדסקטופ הכל בפס) */}
        {!desktop && (
          <header className="sticky top-0 z-20 border-b border-border bg-background">
            <div className="flex h-16 items-center gap-2 px-3">
              <Button variant="ghost" size="icon" aria-label={T.shell.menu} aria-expanded={menuOpen} onClick={() => setMenuOpen(true)}>
                <IconMenu size={22} />
              </Button>
              <Link to="/artifacts" aria-label={T.shell.home} className="flex min-h-11 items-center">
                <Logo className="h-7" />
              </Link>
              <div className="ms-auto flex items-center gap-1">
                <Link
                  to={consentHref(loc.search, 'all')}
                  aria-label={T.shell.consents}
                  title={T.shell.consents}
                  className="inline-flex min-h-11 min-w-11 items-center justify-center gap-2 rounded-sm px-2 text-sm font-medium text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
                >
                  <IconInbox size={20} />
                </Link>
                <ThemeToggle />
              </div>
            </div>
          </header>
        )}
        {/* בלי תקרת רוחב באף עמוד (web-screen-budget) — טבלאות, גרפים ורשימות ברוחב מלא; פרוזה מוגבלת ל-48rem בעמוד עצמו */}
        <main id="main" tabIndex={-1} className={cn('w-full flex-1 px-4 pt-5 pb-8 outline-none md:px-6 md:pt-6', !fullScreen && 'max-md:pb-28')}>
          <ErrorBoundary resetKey={loc.pathname}>
            <Outlet />
          </ErrorBoundary>
        </main>
      </div>

      {/* תפריט מובייל: משתמש, ניווט מלא, יציאה */}
      <Sheet open={menuOpen} onClose={() => setMenuOpen(false)} title={T.shell.menu} side="start" className="max-w-[20rem]">
        <div className="flex items-center justify-between border-b border-border p-4">
          <Logo className="h-8" />
          <Button variant="ghost" size="sm" onClick={() => setMenuOpen(false)}>
            {T.shell.closeMenu}
          </Button>
        </div>
        <div className="flex flex-1 flex-col gap-4 p-4">
          <nav aria-label={T.shell.menu}>
            <ul className="m-0 flex list-none flex-col gap-1 p-0">
              {items.map(({ to, label, Icon }) => (
                <li key={to}>
                  <NavLink
                    to={to}
                    className={({ isActive }) =>
                      cn('flex min-h-12 items-center gap-3 rounded-sm px-3 font-semibold', isActive ? 'bg-primary-soft text-primary' : 'text-foreground hover:bg-muted')
                    }
                  >
                    <Icon size={20} />
                    {label}
                  </NavLink>
                </li>
              ))}
            </ul>
          </nav>
          {/* web-instance-identity: בתחתית — המשתמש, מתחתיו לוגו החברה ושמה, ויציאה */}
          <div className="mt-auto flex flex-col gap-2.5 border-t border-line pt-3" data-testid="drawer-identity">
            <UserCard tone="plain" />
            <CompanyLogo brand={brand} size={96} />
            <Link to={{ search: `?${NOTIFY_PARAM}=1` }} onClick={() => setMenuOpen(false)} className="flex min-h-11 cursor-pointer items-center gap-3 rounded-sm px-3 font-semibold text-foreground hover:bg-muted" data-testid="notify-open-mobile">
              <IconBell size={20} />
              {T.push.nav}
            </Link>
            <button type="button" onClick={() => logout()} className="flex min-h-11 cursor-pointer items-center gap-3 rounded-sm px-3 font-semibold text-foreground hover:bg-muted">
              <IconLogout size={20} />
              {T.logout}
            </button>
          </div>
        </div>
      </Sheet>

      {/* ?consent=<id> מכל נתיב (11 §1) */}
      <ConsentDrawer />
      {/* ?mail=… מכל נתיב (20): "חיבור Gmail" — נפתח כשצריך, נעלם כשמסיימים */}
      <MailConnectDrawer />
      <NotificationsDrawer />
    </div>
  );
}
